import React from "react";
import { Check, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const checkListItems = ["Confidential executive consultation", "Direct engagement with managing partners", "Customized institutional scope & roadmap"];
export default function DiscoverySprintCTA() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="discovery-sprint" className="bg-primary text-primary-foreground py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`rounded-3xl border border-white/10 bg-white/5 p-8 md:p-12 lg:p-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left column */}
            <div className="lg:col-span-7 flex flex-col items-start">
              {/* Accent dashes */}
              <div className="flex items-center gap-2 mb-6" aria-hidden="true">
                <span className="w-8 h-1 rounded-full bg-ring" />
                <span className="w-8 h-1 rounded-full bg-secondary" />
              </div>

              {/* Pill badge */}
              <span className="inline-block rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-ring mb-6">
                INSTITUTIONAL PARTNERSHIP
              </span>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6 leading-tight">
                Start with a Discovery Sprint
              </h2>

              <p className="text-lg text-primary-foreground/80 leading-relaxed mb-8 max-w-xl">
                A focused, time-boxed engagement that maps your institution&apos;s
                growth opportunity and defines the capability your team will own
                at the end of it.
              </p>

              {/* Check-icon list */}
              <ul className="space-y-4">
                {checkListItems.map((item, idx) => <li key={idx} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ring/20 text-ring shrink-0">
                      <Check className="h-4 w-4 stroke-[2.5]" />
                    </span>
                    <span className="text-base text-primary-foreground font-medium">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Right column: DIRECT INQUIRIES box */}
            <div className="lg:col-span-5 w-full">
              <div className="rounded-2xl border border-white/15 bg-primary p-6 md:p-8 shadow-xl flex flex-col">
                <span className="text-xs font-bold uppercase tracking-widest text-ring mb-2">
                  DIRECT INQUIRIES
                </span>
                <p className="text-primary-foreground/80 text-sm mb-6">
                  Connect directly with our advisory leadership.
                </p>

                {/* Buttons */}
                <div className="flex flex-col gap-3 mb-8">
                  <Button asChild size="lg" className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-12 shadow-sm justify-center">
                    <Link to="/ContactPage">
                      Start with a Discovery Sprint <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold h-12 justify-center">
                    <Link to="/ProfessionalServicesPage">
                      Explore our Professional Services
                    </Link>
                  </Button>
                </div>

                {/* Contact rows with icons */}
                <div className="border-t border-white/10 pt-6 space-y-4 text-sm">
                  <a href="mailto:contact@edustrategygroup.com" className="flex items-center gap-3 text-primary-foreground/90 hover:text-white transition-colors">
                    <Mail className="h-4 w-4 text-ring shrink-0" />
                    <span>contact@edustrategygroup.com</span>
                  </a>

                  <a href="tel:+13038078870" className="flex items-center gap-3 text-primary-foreground/90 hover:text-white transition-colors">
                    <Phone className="h-4 w-4 text-ring shrink-0" />
                    <span>(303) 807-8870</span>
                  </a>

                  <div className="flex items-start gap-3 text-primary-foreground/80">
                    <MapPin className="h-4 w-4 text-ring shrink-0 mt-0.5" />
                    <span>Colorado, California, Oregon and New Jersey locations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}